/**
 * Чистые части виртуальной таблицы плотностей (design stage9-table-virtual):
 * что рисовать — строки окна и распорки на месте непоказанных, — и высота
 * строки. Тестируются без DOM.
 */

/**
 * Высота строки таблицы: ячейки `p-2` (по 0,5rem сверху и снизу) вокруг
 * кнопок действий `size-6` (1,5rem) — самого высокого в строке — и нижняя
 * черта 1px (Д2). Строки не меряются: поменял классы строки — поменяй числа,
 * иначе строки поедут относительно распорок. Сверка — e2e `table-virtual`.
 */
export const TABLE_ROW_HEIGHT = {
  /** Содержимое и отступы ячейки, в rem. */
  rem: 2.5,
  /** Нижняя черта строки `border-b`, в px. */
  borderPx: 1,
} as const;

export function tableRowHeightPx(remPx: number): number {
  return TABLE_ROW_HEIGHT.rem * remPx + TABLE_ROW_HEIGHT.borderPx;
}

/**
 * Индексы, которые рисуются: окно виртуализатора плюс строка — точка
 * табуляции и её соседи (Д4). По возрастанию, без повторов, в пределах `count`.
 */
export function withPinnedRows(
  windowIndexes: readonly number[],
  pinnedIndex: number,
  count: number,
): number[] {
  const indexes = new Set(windowIndexes);
  for (let index = pinnedIndex - 1; index <= pinnedIndex + 1; index += 1) {
    if (index >= 0 && index < count) {
      indexes.add(index);
    }
  }
  return [...indexes].sort((first, second) => first - second);
}

/** Нарисованная строка в координатах виртуализатора: `start` уже включает `scrollMargin`. */
export interface WindowItem {
  index: number;
  start: number;
  end: number;
}

export type WindowPart =
  | { kind: "row"; index: number }
  | { kind: "spacer"; key: string; height: number };

/**
 * Состав `<tbody>`: нарисованные строки по порядку и распорки на месте
 * непоказанных — до первой, между несмежными (точка табуляции вне окна) и
 * после последней (Д1). Распорка нулевой высоты не рисуется: короткая
 * таблица остаётся без распорок вовсе.
 */
export function windowParts(
  items: readonly WindowItem[],
  scrollMargin: number,
  totalSize: number,
): WindowPart[] {
  const parts: WindowPart[] = [];
  let cursor = scrollMargin;
  for (const item of items) {
    const gap = item.start - cursor;
    if (gap > 0) {
      parts.push({ kind: "spacer", key: `spacer-before-${item.index}`, height: gap });
    }
    parts.push({ kind: "row", index: item.index });
    cursor = item.end;
  }
  const tail = scrollMargin + totalSize - cursor;
  if (tail > 0) {
    parts.push({ kind: "spacer", key: "spacer-end", height: tail });
  }
  return parts;
}
